import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { MdStar, MdCheckCircle } from 'react-icons/md';
import { postPropertyRating } from '../../../api/services/propertyService';
import './PropertyRatingModal.css';

const RATING_LABELS = {
  1: 'Poor',
  2: 'Fair',
  3: 'Average',
  4: 'Good',
  5: 'Excellent'
};

const PropertyRatingModal = ({ isOpen, onClose, propertyId, propertyName }) => {
  const [score, setScore] = useState(0);
  const [hoveredScore, setHoveredScore] = useState(0);
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  if (!isOpen) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (score === 0) {
      setError('Please select a rating score.');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      await postPropertyRating(propertyId, { score, message });
      setSuccess(true);
      setTimeout(() => {
        onClose();
        // Reset state after closing
        setScore(0);
        setMessage('');
        setSuccess(false);
      }, 2000);
    } catch (err) {
      setError(err.message || 'Failed to submit rating. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const currentLabel = RATING_LABELS[hoveredScore || score] || 'Select a rating';

  return createPortal(
    <div className="rating-modal-overlay" onClick={onClose}>
      <div className="rating-modal-content" onClick={(e) => e.stopPropagation()}>
        {!success ? (
          <>
            <div className="rating-modal-header">
              <h2 className="rating-modal-title">Rate Property</h2>
              <p className="rating-modal-subtitle">
                How was your experience at <strong>{propertyName || 'this property'}</strong>?
              </p>
            </div>

            <form onSubmit={handleSubmit}>
              <div className="rating-stars-container">
                {[1, 2, 3, 4, 5].map((num) => (
                  <button
                    key={num}
                    type="button"
                    className={`rating-star-btn ${
                      num <= (hoveredScore || score) ? (hoveredScore ? 'hovered' : 'active') : ''
                    }`}
                    onMouseEnter={() => setHoveredScore(num)}
                    onMouseLeave={() => setHoveredScore(0)}
                    onClick={() => setScore(num)}
                    aria-label={`${num} stars`}
                  >
                    <MdStar />
                  </button>
                ))}
              </div>

              <div className="rating-label">{currentLabel}</div>

              <div className="rating-form-group">
                <label className="rating-form-label" htmlFor="rating-message">
                  Your review (optional)
                </label>
                <textarea
                  id="rating-message"
                  className="rating-textarea"
                  placeholder="Share your experience with the management, amenities, or environment..."
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  disabled={loading}
                />
              </div>

              {error && <div className="rating-error">{error}</div>}

              <div className="rating-modal-actions">
                <button
                  type="button"
                  className="rating-cancel-btn"
                  onClick={onClose}
                  disabled={loading}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="rating-submit-btn"
                  disabled={loading || score === 0}
                >
                  {loading ? 'Submitting...' : 'Submit Rating'}
                </button>
              </div>
            </form>
          </>
        ) : (
          <div className="rating-success-state">
            <MdCheckCircle className="rating-success-icon" />
            <h2 className="rating-modal-title">Thank You!</h2>
            <p className="rating-modal-subtitle">Your rating has been saved successfully.</p>
          </div>
        )}
      </div>
    </div>,
    document.body
  );
};

export default PropertyRatingModal;
